import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  getRecommendedFileTypesRoutine,
  reclassifyFileRoutine
} from 'ducks/files/actions'
import { selectRecommendedFileTypes, selectRecommendedFileTypesMessage } from 'ducks/files/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import { pathOr } from 'ramda'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const ReclassifyFileModal = ({ open, onClose, file, onOpenEditModal, bp: bpProp = null }) => {
  const dispatch = useDispatch()
  const [selectedFileTypeId, setSelectedFileTypeId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const recommendedFileTypes = useSelector(selectRecommendedFileTypes)
  const recommendedFileTypesMessage = useSelector(selectRecommendedFileTypesMessage)
  const fileCategories = useSelector(selectFileCategories)
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const lastRequestedFileId = useRef(null)

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY

      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'

      return () => {
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [open])

  useEffect(() => {
    if (open && file?.id && lastRequestedFileId.current !== file.id) {
      lastRequestedFileId.current = file.id
      setIsLoading(true)
      dispatch(
        getRecommendedFileTypesRoutine({
          fileId: file.id
        })
      )
    } else if (!open) {
      // Clear everything when modal closes
      lastRequestedFileId.current = null
      setSelectedFileTypeId(null)
      setIsLoading(false)
    }
  }, [open, file?.id, dispatch])

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFileTypeId(null)
      setIsLoading(false)
    }
  }, [open])

  useEffect(() => {
    if (recommendedFileTypes.length > 0 && lastRequestedFileId.current === file?.id) {
      setIsLoading(false)
    }
  }, [recommendedFileTypes, file?.id])

  const handleFileTypeSelect = fileTypeId => {
    setSelectedFileTypeId(fileTypeId)
  }

  const handleSubmit = () => {
    if (selectedFileTypeId && file?.id && bp?.id) {
      // Close modal first to prevent any interference
      setSelectedFileTypeId(null)
      lastRequestedFileId.current = null
      onClose()

      // Then dispatch the action
      dispatch(
        reclassifyFileRoutine({
          fileId: file.id,
          fileTypeId: selectedFileTypeId,
          bpId: bp.id
        })
      )
    }
  }

  const handleCancel = () => {
    setSelectedFileTypeId(null)
    lastRequestedFileId.current = null // Clear to allow re-fetching
    onClose()
  }

  const handleManualAmend = () => {
    onClose()
    if (onOpenEditModal && file) {
      onOpenEditModal(file)
    }
  }

  const getCategoryName = fileCategoryId => {
    const category = fileCategories?.find(cat => cat.id === fileCategoryId)
    return category ? category.name : 'Unknown Category'
  }

  const currentFileType = pathOr('', ['fileType', 'name'], file)

  if (!open) return null

  return (
    <Backdrop onClick={handleCancel}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        {/* Mobile Handle */}
        <MobileHandle />

        {/* Header */}
        <ModalHeader>
          <HeaderLeft>
            <HeaderIcon>
              <AutoAwesomeIcon />
            </HeaderIcon>
            <HeaderText>
              <HeaderTitle>Reclassify File</HeaderTitle>
              <HeaderFileName>{pathOr('Untitled', ['displayName'], file)}</HeaderFileName>
            </HeaderText>
          </HeaderLeft>
          <CloseButton onClick={handleCancel}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {/* Current Type Info */}
        <CurrentTypeBar>
          <CurrentTypeLabel>Current type:</CurrentTypeLabel>
          <CurrentTypeValue>{currentFileType || 'Unknown'}</CurrentTypeValue>
        </CurrentTypeBar>

        {/* Description Message */}
        <DescriptionBar>
          <DescriptionText>
            Guru has analysed your document and suggests these alternative file types. Each recommendation includes a confidence score and reasoning based on the document content. Review the suggestions below and select the most appropriate classification for your file.
          </DescriptionText>
        </DescriptionBar>

        {/* Content */}
        <ModalContent>
          {isLoading ? (
            <LoadingContainer>
              <Spinner />
              <LoadingText>Analysing document with AI...</LoadingText>
              <LoadingSubtext>This may take a few seconds</LoadingSubtext>
            </LoadingContainer>
          ) : isNotNilOrEmpty(recommendedFileTypes) ? (
            <>
              <SectionHeader>
                <SectionTitle>AI Recommendations</SectionTitle>
                <SectionSubtitle>Select the best match for your file</SectionSubtitle>
              </SectionHeader>

              <RecommendationsList>
                {recommendedFileTypes.slice(0, 10).map((recommendation, index) => (
                  <RecommendationCard
                    key={recommendation.fileTypeId}
                    selected={selectedFileTypeId === recommendation.fileTypeId}
                    onClick={() => handleFileTypeSelect(recommendation.fileTypeId)}
                  >
                    <CardLeft>
                      <RankBadge selected={selectedFileTypeId === recommendation.fileTypeId}>
                        {selectedFileTypeId === recommendation.fileTypeId ? (
                          <CheckCircleIcon />
                        ) : (
                          index + 1
                        )}
                      </RankBadge>
                      <CardInfo>
                        <CardTitle>{recommendation.fileTypeName}</CardTitle>
                        {recommendation.reasoning && (
                          <CardReasoning>{recommendation.reasoning}</CardReasoning>
                        )}
                      </CardInfo>
                    </CardLeft>
                    <ConfidenceBadge confidence={recommendation.confidence}>
                      {Math.round(recommendation.confidence * 100)}%
                    </ConfidenceBadge>
                  </RecommendationCard>
                ))}
              </RecommendationsList>
            </>
          ) : (
            <EmptyState>
              <EmptyIcon>🔍</EmptyIcon>
              <EmptyTitle>No recommendations</EmptyTitle>
              <EmptyText>
                {recommendedFileTypesMessage || "AI couldn't find alternative classifications for this file."}
              </EmptyText>
            </EmptyState>
          )}
        </ModalContent>

        {/* Footer Actions */}
        <ModalFooter>
          <FooterButtonsLeft>
            <SecondaryButton onClick={handleManualAmend}>
              Edit Manually
            </SecondaryButton>
          </FooterButtonsLeft>
          <FooterButtonsRight>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            {isNotNilOrEmpty(recommendedFileTypes) && (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!selectedFileTypeId}
              >
                Apply
              </PrimaryButton>
            )}
          </FooterButtonsRight>
        </ModalFooter>
      </ModalContainer>
    </Backdrop>
  )
}

export default ReclassifyFileModal

// Professional Modal Styling
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
  animation: fadeIn 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-end;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`

const MobileHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 36px;
    height: 4px;
    background: #d1d5db;
    border-radius: 2px;
    margin: 10px auto 6px;
    flex-shrink: 0;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #34bdcc 0%, #2da8b6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 22px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 8px;

    svg {
      font-size: 20px;
    }
  }
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const HeaderFileName = styled.div`
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  svg {
    font-size: 20px;
    color: #6b7280;
  }

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    transform: scale(0.95);
  }
`

const CurrentTypeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 10px 16px;
  }
`

const CurrentTypeLabel = styled.span`
  font-size: 13px;
  color: #6b7280;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const CurrentTypeValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  background: #e5e7eb;
  padding: 4px 10px;
  border-radius: 6px;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 3px 8px;
  }
`

const DescriptionBar = styled.div`
  padding: 16px 24px;
  background: #f0fdf4;
  border-bottom: 1px solid #bbf7d0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`

const DescriptionText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #166534;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 1.4;
  }
`

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const SectionHeader = styled.div`
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`

const SectionSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const RecommendationCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border: 2px solid ${({ selected }) => selected ? '#34bdcc' : '#e5e7eb'};
  border-radius: 12px;
  background: ${({ selected }) => selected ? 'rgba(52, 189, 204, 0.05)' : 'white'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #34bdcc;
    background: rgba(52, 189, 204, 0.05);
  }

  &:active {
    transform: scale(0.99);
  }

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 10px;
  }
`

const CardLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 10px;
  }
`

const RankBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ selected }) => selected ? '#34bdcc' : '#f3f4f6'};
  color: ${({ selected }) => selected ? 'white' : '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.2s;

  svg {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    border-radius: 6px;

    svg {
      font-size: 16px;
    }
  }
`

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const CardReasoning = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const ConfidenceBadge = styled.div`
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${({ confidence }) => {
    if (confidence >= 0.8) return '#d1fae5';
    if (confidence >= 0.5) return '#fef3c7';
    return '#fee2e2';
  }};
  color: ${({ confidence }) => {
    if (confidence >= 0.8) return '#059669';
    if (confidence >= 0.5) return '#d97706';
    return '#dc2626';
  }};

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    border-radius: 6px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #34bdcc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const LoadingSubtext = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 40px;
    margin-bottom: 12px;
  }
`

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const EmptyText = styled.div`
  font-size: 13px;
  color: #6b7280;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #f3f4f6;
  background: #f9fafb;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    flex-direction: column;
    gap: 10px;
  }
`

const FooterButtonsLeft = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`

const FooterButtonsRight = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`

const SecondaryButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
  }
`

const CancelButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
  }
`

const PrimaryButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => disabled ? '#d1d5db' : '#34bdcc'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2da8b6;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
  }
`
